import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import { Box, Popover, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import PatientQuickActions from 'src/shared/patientActions/PatientQuickActions'
import {
  canDownloadSupportReportForEventType,
  getSupportReportAccessConfig,
} from 'src/supportPortal/access/accessMatrix'
import SupportSendClinicalDocumentDialog from 'src/supportPortal/components/bookings/SupportSendClinicalDocumentDialog'
import SupportSendReportToPractitionerDialog from 'src/supportPortal/components/bookings/SupportSendReportToPractitionerDialog'
import { getUserRoles } from 'src/mentor/layout/utils'

// The single destination role for each report type (null = multiple, so the button stays generic and the
// dialog's role sub-tabs disambiguate). Mirrors the backend recipientRolesForReport rules.
const RECIPIENT_TARGET_LABEL = {
  prescription: 'Therapist',
  'therapy-report': 'Doctor',
  'screening-report': 'Doctor',
  'assessment-report': null,
  'diet-plan': null,
}

// The full set of patient quick-actions the support user has, in the order product asked for. The
// "Send Prescription" slot is report-type-aware: it only appears when the session's clinical document
// exists (supportContext.supportReport) and the user can download that report type, and it is relabelled
// per event type (Send Therapy Report / Screening Report / Assessment Report / Prescription).
// Send Payment Link intentionally lives in the Billing menu, not here.
const SUPPORT_ACTIONS_WITH_DOC = [
  'send-prescription',
  'send-to-practitioner',
  'book-slot',
  'bill-patient',
  'send-review',
  'send-attachment',
]
const SUPPORT_ACTIONS_WITHOUT_DOC = SUPPORT_ACTIONS_WITH_DOC.filter(
  (id) => id !== 'send-prescription' && id !== 'send-to-practitioner',
)

// The shared patient quick-actions surfaced on the support booked-events card behind one common button,
// mirroring the mentor session-end panel. Uses the 'support-patient' source so payment/bill run on the
// booking practitioner's behalf; the clinical DOCUMENT send goes out from AUMHUM (report-type-aware).
export default function SupportPatientActionsButton({ bookedEvent, supportContext, onBookSlot }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [docDialogOpen, setDocDialogOpen] = useState(false)
  const [practitionerDialogOpen, setPractitionerDialogOpen] = useState(false)

  // The session's clinical document is available to send when the card resolved its report action
  // (report exists + session started + role) AND this user can download that report type. Relabel the
  // send action to the event type's report so a therapy session says "Send Therapy Report", etc.
  const clinicalDocument = useMemo(() => {
    if (!supportContext?.supportReport) {
      return null
    }
    const config = getSupportReportAccessConfig(bookedEvent?.eventType)
    if (!config?.reportType) {
      return null
    }
    const userRoles = getUserRoles() || []
    if (!canDownloadSupportReportForEventType(userRoles, bookedEvent?.eventType)) {
      return null
    }
    return {
      reportType: config.reportType,
      documentName: config.title || 'Report',
      label: `Send ${config.title || 'Report'}`,
    }
  }, [supportContext?.supportReport, bookedEvent?.eventType])

  // "Send to Doctor" / "Send to Therapist" for a single target role; generic when the report can go to
  // several (the dialog's role sub-tabs disambiguate). Only offered when a report is available.
  const practitionerSendLabel = useMemo(() => {
    if (!clinicalDocument) {
      return undefined
    }
    const roleLabel = RECIPIENT_TARGET_LABEL[clinicalDocument.reportType]
    return roleLabel ? `Send to ${roleLabel}` : 'Send to a practitioner'
  }, [clinicalDocument])

  const contactContext = useMemo(
    () => ({
      source: 'support-patient',
      bookedEventId: bookedEvent?.id,
      patientId: bookedEvent?.patientId,
      clinicBrandId: bookedEvent?.clinicBrandId,
      clinicId: bookedEvent?.clinicId,
      clinicBrandName: bookedEvent?.clinicBrandName,
      patientName: bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient',
      patientMobile: bookedEvent?.patientInfoData?.mobile,
      patientEmail: bookedEvent?.patientInfoData?.email,
      clinicalDocument,
    }),
    [bookedEvent, clinicalDocument],
  )

  if (!bookedEvent?.id) {
    return null
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setAnchorEl(event.currentTarget)
        }}
        title="Patient actions: send report, payment link, bill, review, attachment"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          appearance: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 12.5,
          fontWeight: 650,
          lineHeight: 1,
          px: 1.1,
          py: 0.6,
          borderRadius: '8px',
          border: '1px solid rgba(67, 56, 202, 0.28)',
          color: 'rgba(67, 56, 202, 0.95)',
          backgroundColor: 'rgba(67, 56, 202, 0.04)',
          '&:hover': { backgroundColor: 'rgba(67, 56, 202, 0.09)', borderColor: 'rgba(67, 56, 202, 0.5)' },
        }}
      >
        <PersonOutlineRoundedIcon sx={{ fontSize: 15 }} />
        Patient actions
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { borderRadius: '14px', p: 1.5, width: 290, maxWidth: '92vw', maxHeight: '80vh', overflowY: 'auto' } },
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#64748b', px: 0.5, pb: 1, letterSpacing: '0.03em' }}>
          PATIENT ACTIONS
        </Typography>
        <PatientQuickActions
          contactContext={contactContext}
          onBookSlot={onBookSlot ? () => onBookSlot(bookedEvent) : undefined}
          availableActions={clinicalDocument ? SUPPORT_ACTIONS_WITH_DOC : SUPPORT_ACTIONS_WITHOUT_DOC}
          clinicalDocumentLabel={clinicalDocument?.label}
          onClinicalDocument={() => {
            setAnchorEl(null)
            setDocDialogOpen(true)
          }}
          practitionerSendLabel={practitionerSendLabel}
          onPractitionerSend={() => {
            setAnchorEl(null)
            setPractitionerDialogOpen(true)
          }}
          layout="stack"
          dense
        />
      </Popover>

      <SupportSendClinicalDocumentDialog
        open={docDialogOpen}
        onClose={() => setDocDialogOpen(false)}
        contactContext={contactContext}
      />

      <SupportSendReportToPractitionerDialog
        open={practitionerDialogOpen}
        onClose={() => setPractitionerDialogOpen(false)}
        contactContext={contactContext}
      />
    </>
  )
}
